/**
 * GARANTÍA SUELTA — reglas PURAS del formulario (pedido del cliente en la
 * llamada grabada).
 *
 * Abrir la garantía de un artículo que NO está en el sistema tiene que ser
 * fácil: si el formulario pelea, la oficina vuelve al atajo viejo —un trabajo
 * normal con la casilla "Garantía" marcada— y ahí se pierden el plazo de
 * atención, la prioridad del cliente sin acceso, la tipificación al cerrar y el
 * lugar en los reportes.
 *
 * Por eso la división es clara:
 *   - Lo que NO puede faltar: de quién es, qué reporta y cuándo se atiende.
 *   - Lo que se AVISA pero no traba: modelo, número de serie y fecha de
 *     instalación. Sin ellos la garantía nace igual; lo que se pierde es poder
 *     decir si el artículo está o no dentro de cobertura.
 *
 * Las reglas duras viven en el backend; esto existe para que la pantalla pueda
 * decir QUÉ falta antes de que el operador apriete el botón.
 */

/** Lo que el formulario tiene cargado al momento de validar. */
export interface FormularioGarantiaSuelta {
  descripcion: string;
  /** Valor crudo del `datetime-local` de "¿Cuándo se atiende?". */
  cuando: string;
  /** Se atiende por teléfono: no lleva cita ni técnico. */
  remoto: boolean;
  /**
   * "El artículo se instaló antes de que existiera el sistema". Marcarla es
   * decir "tengo los datos de esa instalación", y ahí sí se piden los tres:
   * a medias no sirven para calcular cobertura.
   */
  previa: boolean;
  modelo: string;
  numeroSerie: string;
  /** Valor crudo del `date` de "Fecha de instalación" (YYYY-MM-DD). */
  fechaInstalacion: string;
}

/** ¿Hay texto real (no solo espacios)? */
const lleno = (v: string): boolean => v.trim() !== '';

/**
 * Qué impide abrir la garantía, en el idioma del operador. `null` = nada.
 * El orden es el de la pantalla: se reclama lo primero que falta, de arriba
 * abajo, para que el usuario no tenga que buscar dónde está el problema.
 */
export function validarGarantiaSuelta(f: FormularioGarantiaSuelta): string | null {
  if (!lleno(f.descripcion)) return 'Escriba qué problema reporta el cliente.';

  // Los datos del equipo solo se exigen cuando el operador dijo tenerlos.
  if (f.previa && (!lleno(f.modelo) || !lleno(f.numeroSerie) || !lleno(f.fechaInstalacion))) {
    return 'Para una instalación anterior al sistema hacen falta modelo, serie y fecha.';
  }

  // Una fecha futura no es un dato incompleto: es un dato equivocado, y con ella
  // la cobertura diría cualquier cosa.
  if (lleno(f.fechaInstalacion)) {
    const fecha = new Date(f.fechaInstalacion);
    if (!Number.isNaN(fecha.getTime()) && fecha.getTime() > Date.now()) {
      return 'La fecha de instalación no puede ser futura.';
    }
  }

  // Un caso por teléfono no lleva visita: pedirle cita sería inventar una agenda
  // que nadie va a cumplir.
  if (!f.remoto && !lleno(f.cuando)) {
    return 'Indique el día y la hora en que se va a atender la garantía.';
  }

  return null;
}

/**
 * De qué se le avisa al operador por dejar datos vacíos. Lista vacía = nada que
 * decir. Son AVISOS: la garantía se abre igual.
 */
export function avisosGarantiaSuelta(f: FormularioGarantiaSuelta): string[] {
  const avisos: string[] = [];
  if (!lleno(f.modelo) || !lleno(f.fechaInstalacion)) {
    avisos.push(
      'Sin el modelo y la fecha de instalación no se puede saber si el artículo está en garantía.',
    );
  }
  if (!lleno(f.numeroSerie)) {
    avisos.push('Sin el número de serie no se va a poder seguir el historial de ese equipo.');
  }
  return avisos;
}
